import { NuvoGrid } from '@nuvolo/nuux-core/components/NuvoGrid';
import styled from 'styled-components';
import { color } from '@nuvolo/nuux-theme/utils';
import useAttachmentManager, { MAX_TOTAL_STORAGE_BYTES } from './hooks/useAttachmentManager';
import { useCallback, useMemo, useState } from 'react';
import useToast from '#/common/hooks/useToast';
import FileUpload, { FileInfo } from '../FileUpload';
import { FileStatus } from '../FileUpload/FileUploadProps';

const getAttachmentStatus = (
  attachmentId: string,
  currentlyUploading: { id: string }[],
  currentlyDeleting: string[],
  currentlyOpening: string[],
): FileStatus => {
  const isUploading = currentlyUploading.some((u) => u.id === attachmentId);
  if (isUploading) return 'uploading';

  const isDeleting = currentlyDeleting.includes(attachmentId);
  if (isDeleting) return 'deleting';

  const isOpening = currentlyOpening.includes(attachmentId);
  if (isOpening) return 'opening';

  return 'uploaded';
};

const Styles = styled.div`
  .dx-button-content {
    color: ${color('custom', 'contrastText')} !important;
  }

  .wrapper {
    width: 100%;
    padding: 10px 20px;
  }

  .caption {
    margin: 40px 16px 16px 16px !important;
    padding: 20px 10px 10px !important;
    font-size: 20px;
    font-weight: 500;
    line-height: 26px;
    height: 26px;
    color: ${color('primary', 'dark')};
  }

  .storage-warning {
    border: 1px solid ${color('warn', 'middle')};
    background-color: ${color('warn', 'middle', 0.05)};
    color: ${color('primary', 'dark')};
    border-radius: 16px;
    padding: 12px 24px;
  }

  .btns-wrapper {
    border: 1px solid ${color('primary', 'light3')} !important;
    width: 100% !important;
    padding: 20px !important;
    margin: 0 !important;
    border-radius: 20px;
    background-color: ${color('custom', 'dataGridBackground')};
  }

  .dx-button {
    border-radius: 16px;
    padding: 12px 24px;
    background-color: ${color('ui', 'light2')};
    border: none !important;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: ${color('primary', 'dark')};
    width: 220px;
    height: 44px;
  }

  .dx-fileuploader-input-label {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }
`;

export const AttachmentUpload = ({
  relatedTable,
  relatedItem,
  disableUpload,
  tenantId,
}: {
  relatedTable: string;
  relatedItem: string;
  disableUpload?: boolean;
  tenantId?: string | null;
}) => {
  const {
    selectedFiles: file,
    onPickFile,
    upload,
    open,
    remove,
    currentlyDeleting,
    currentlyUploading,
    currentAttachments,
    totalUsedBytes,
    isStorageFull,
    loadingAttachments,
  } = useAttachmentManager({
    relatedTable,
    relatedItem,
    attachmentType: 'general',
    tenantId,
  });

  const { alert, confirm } = useToast();

  const handleUpload = useCallback(
    async (filesToUpload: File[]) => {
      const filesSize = filesToUpload.reduce((acc, file) => acc + file.size, 0);
      const projectedBytes = totalUsedBytes + filesSize;
      const projectedPercent = Math.round((projectedBytes / MAX_TOTAL_STORAGE_BYTES) * 100);

      if (projectedPercent >= 99) {
        void alert(
          'error',
          'Storage limit reached. Uploading this file would exceed your organization storage quota. Please remove old files or contact your administrator.',
        );
        return;
      }

      if (projectedPercent >= 80) {
        const confirmed = await confirm(
          'warning',
          `Uploading this file will bring your storage to ${projectedPercent}% of your organization quota. Do you want to continue?`,
          ['Cancel', 'Upload anyway'],
        );
        if (!confirmed) return;
      }

      try {
        await upload(filesToUpload);
      } catch (err: any) {
        void alert('error', err?.message || 'Failed to upload file. Please try again.');
      }
    },
    [upload, alert, confirm, file, totalUsedBytes],
  );

  const handleFileChanged = useCallback(
    (filesSelected: File[]) => {
      try {
        onPickFile(filesSelected);
        handleUpload(filesSelected);
      } catch (err: any) {
        void alert('error', err?.message || 'Failed to select file.');
      }
    },
    [onPickFile, alert, handleUpload],
  );

  const handleDeleteClick = useCallback(
    async (attachmentId: string, fileName: string) => {
      if (disableUpload) {
        return;
      }

      const sure = await confirm(
        'warning',
        `Are you sure you want to delete the ${fileName} file?`,
      );
      if (!sure) return;

      void remove({ id: attachmentId, originalFileName: fileName });
    },
    [confirm, disableUpload, remove],
  );

  const [openingFileId, setOpeningFileId] = useState<string | null>(null);
  const handleFileOpen = useCallback(async ({ id }: FileInfo) => {
    if (!id) return;

    setOpeningFileId(id);
    await open(id);
    setOpeningFileId(null);
  }, []);

  const attachments = useMemo<FileInfo[]>(() => {
    return [...currentAttachments, ...currentlyUploading].map<FileInfo>((a) => {
      const status = getAttachmentStatus(
        a.id,
        currentlyUploading,
        currentlyDeleting,
        openingFileId ? [openingFileId] : [],
      );

      return {
        id: a.id,
        name: a.originalFileName ?? '',
        size: a.size ?? undefined,
        mimeType: a.mimeType ?? undefined,
        status,
      };
    });
  }, [currentlyUploading, currentAttachments, currentlyDeleting, openingFileId]);

  return (
    <Styles>
      <span className="caption">File Attachments</span>
      <div className="wrapper">
        <NuvoGrid container className="btns-wrapper">
          {isStorageFull && (
            <div className="storage-warning">
              Storage limit reached. Uploading files would exceed your organization storage quota.
              Please remove old files or contact your administrator.
            </div>
          )}
          <FileUpload
            files={attachments}
            onFileOpen={handleFileOpen}
            onFileSelect={handleFileChanged}
            onFileDelete={({ id, name }) => {
              if (!id) return;
              void handleDeleteClick(id, name);
            }}
            deleteTxt="Delete Attachment"
            disableUpload={disableUpload || isStorageFull}
            disableDelete={disableUpload}
            loading={loadingAttachments}
          />
        </NuvoGrid>
      </div>
    </Styles>
  );
};

export default AttachmentUpload;
